/**
 * Trailing utilisation — what the machines actually DID, beside four numbers about what
 * they are being asked to do. It is the fifth KPI and the only backward-looking one, which
 * is why it carries its own sparkline rather than a bare value: a single month says nothing
 * about whether the estate is getting busier.
 *
 * It replaces BOTH the by-month table and the trend chart the page used to carry. The
 * basis-comparison table is deliberately gone: which denominator utilisation is measured
 * against is a governance decision (registry/capacity_basis.yaml), and a four-column table
 * made the denominator the subject of a planning page.
 *
 * The end marker is a ZERO-LENGTH round-capped segment, not a <circle> — the viewBox is
 * stretched non-uniformly, and a circle deforms into an ellipse under that.
 */

import React from 'react'
import { useAvailabilitySeries } from '../../hooks/useAvailability'
import type { AvailabilityView } from '../../types/availability'
import type { Site } from '../../types/metrics'

interface Point {
  period: string
  pct: number
}

/** The headline basis is the SERVER's answer, carried on the series (G15). Picking one
 *  here would let this card and the load grid divide by different hours. */
const pctOf = (m: AvailabilityView, basis: string): number | null =>
  m.bases.find((b) => b.basis === basis)?.utilisation_pct ?? null

/** Only months the basis actually serves — a pending month is a gap, not a zero. */
const servedPoints = (months: AvailabilityView[], basis: string): Point[] =>
  months
    .map((m) => ({ period: m.period, pct: pctOf(m, basis) }))
    .filter((p): p is Point => p.pct !== null)

const monthLabel = (period: string): string =>
  new Date(`${period}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short' })

/** 'YYYY-MM' from LOCAL parts — toISOString() is UTC and would mis-key the first hour of a
 *  month during BST. A month at or after this one is INCOMPLETE: its numerator is
 *  month-to-date against a full-month denominator, so it reads low. That is the July "16%"
 *  artefact, and a headline carrying it unflagged is the single most misreadable number the
 *  old page served. */
const monthKey = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`

const yearLabel = (period: string): string =>
  new Date(`${period}-01T00:00:00`).toLocaleDateString('en-GB', { year: '2-digit' })

/** Points → 'x,y' pairs in a 0 0 100 26 box, padded so the line never sits on an edge. */
function polyline(points: Point[]): string[] {
  const values = points.map((p) => p.pct)
  const lo = Math.min(...values)
  const hi = Math.max(...values)
  const pad = (hi - lo) * 0.18 || 1
  const span = hi - lo + pad * 2
  return points.map((p, i) => {
    const x = points.length > 1 ? (i / (points.length - 1)) * 100 : 50
    return `${x.toFixed(1)},${(24 - ((p.pct - lo + pad) / span) * 22).toFixed(1)}`
  })
}

function Spark({ points }: { points: Point[] }): React.JSX.Element {
  const xy = polyline(points)
  return (
    <svg viewBox="0 0 100 26" preserveAspectRatio="none" className="block h-[26px] w-full overflow-visible" aria-hidden="true">
      <polyline points={xy.join(' ')} fill="none" stroke="var(--lc-blue)" strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={xy[xy.length - 1]} fill="none" stroke="var(--lc-blue)" strokeWidth="5" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

function Headline({ last, delta, partial }: {
  last: Point | undefined
  delta: number | null
  partial: boolean
}): React.JSX.Element {
  return (
    <div className="flex items-baseline gap-[7px]">
      <span className="font-sans text-[19px] font-semibold" style={{ color: 'var(--lc-ink)' }}>
        {last ? `${last.pct.toFixed(1)}%` : '—'}
      </span>
      {delta !== null && (
        <span className="font-mono text-[10.5px] font-medium" style={{ color: delta >= 0 ? 'var(--lc-teal)' : 'var(--lc-red)' }}>
          {delta >= 0 ? '+' : ''}{delta.toFixed(1)} pts
        </span>
      )}
      {partial && (
        <span
          title="month-to-date numerator against a full-month denominator — it reads low, and that is not a capacity drop"
          className="font-mono text-[9px] font-semibold uppercase tracking-[.06em]"
          style={{ color: 'var(--lc-amber-ink)' }}
        >
          · to date
        </span>
      )}
    </div>
  )
}

function Foot({ range, open, onToggle }: {
  range: string
  open: boolean
  onToggle: () => void
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="whitespace-nowrap font-mono text-[10px]" style={{ color: 'var(--lc-faint)' }}>{range}</span>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex-none whitespace-nowrap font-mono text-[10px] font-semibold tracking-[.05em]"
        style={{ color: 'var(--lc-blue)' }}
      >
        {open ? 'hide ▴' : 'how ▾'}
      </button>
    </div>
  )
}

export default function UtilisationCard({ site, from, to, open, onToggle }: {
  site: Site
  from: string
  to: string
  open: boolean
  onToggle: () => void
}): React.JSX.Element {
  const { data } = useAvailabilitySeries(site, from, to)
  const months = data?.months ?? []
  const headline = data?.headline_basis ?? ''
  const points = servedPoints(months, headline)
  const last = points[points.length - 1]
  const basis = months[0]?.bases.find((b) => b.basis === headline)?.label ?? headline
  const range = points.length
    ? `${monthLabel(points[0].period)}–${monthLabel(last.period)} ${yearLabel(last.period)}`
    : '—'
  return (
    <div
      className="flex flex-col gap-[3px] rounded-[9px] border px-3 py-2.5"
      style={{ borderColor: 'var(--lc-b09)', background: 'var(--lc-panel)' }}
    >
      <div className="font-mono text-[9.5px] font-semibold uppercase tracking-[.09em]" style={{ color: 'var(--lc-mut)' }}>
        Utilisation · trailing {points.length || ''}mo
      </div>
      <Headline
        last={last}
        delta={points.length > 1 ? last.pct - points[0].pct : null}
        partial={last !== undefined && last.period >= monthKey(new Date())}
      />
      {points.length > 1 && <Spark points={points} />}
      <div className="font-mono text-[10px] leading-[1.35]" style={{ color: 'var(--lc-faint)' }}>{basis} basis</div>
      <Foot range={range} open={open} onToggle={onToggle} />
    </div>
  )
}
